import { memo, useCallback, useMemo } from "react";
import { type SrpProductData } from "../../../../utils/models";
import { Card } from "../../../../Configurators/Linear/components/Card";
import {
  AliasesParameter,
  GPSDParameter,
  OldDesignationParameter,
  SchaefflerIdParameter,
  SearchPageParamContainer,
} from "../../../../common/components/Product/ProductParams";
import { PriceReferenceParam } from "../../../../Cart/components/LineItemEditableParams";
import { QuantityInput } from "../../../../common/components/QuantityInput/QuantityInput";
import { MAX_QUANTITY } from "../../../../utils/constants";
import CircleSpinner from "../../../../common/components/CircleSpinner/CircleSpinner";
import {
  getSurcharge,
  isProductPMA,
  type SelectNetPriceProps,
} from "../../../../feature/ProductDescriptor/v1/productDescriptorUtils";
import { PackagingSelection } from "../../../../common/components/PackagingSelection/PackagingSelection";

import { addToCartApi } from "../../../../AddToCart/redux/addToCart/actions";
import { getCanUserAddToCart } from "../../../../ProductDetails/components/ProductSummary/ExtendedPdpSummary";
import { canSeePriceRefColumn } from "../../../../QuickEntryPopup/helpers";
import { redirectToUrl } from "../../../../utils/url/urlUtils";
import { usePackageRestrictionContext } from "../../../../common/components/PackageRestrictionContext/PackageRestrictionContext";
import {
  isProductComparisonCheckboxVisible,
  ProductComparisonCheckbox,
} from "../../../../ProductComparison/ProductComparisonCheckbox";
import { NewVersionMessage } from "../../../../common/components/Messages/NewVersionMessage";
import { useAddToCartForm } from "../../../../AddToCart/hooks/useAddToCartNew";
import { canSeeAddToCartButton, canSeeListPrice, canSeeWishlistButton } from "../../../../utils/features";
import { trackUserClickRecommendedProductSectionDetails } from "../../../../ProductDetails/RecommendedProducts/recommendedProductAnalytics";

import classes from "./SearchResultProductCustomer.module.less";
import classNames from "classnames";
import CenterContents from "../../../../common/components/CenterContents";
import { isSpecialPrice } from "~/ProductDetails/redux/productDetailsSelectors";
import { useTranslation } from "react-i18next";
import { SRPPackageRestrictionContext } from "~/common/components/PackageRestrictionContext/SRPPackageRestrictionContext";
import { getGALocationForSearchItem, SearchPageType, useSearchPageType } from "~/Search/useSearchPageType";
import {
  CardContent,
  ListPrice,
  ProductDetailsLayout,
} from "~/Search/components/SearchResults/ListView/SearchResultItemCommonElements";
import { SaleTag } from "~/SalesCampaign/components/SaleTag/SaleTag";
import { SalesDataProvider } from "~/SalesCampaign/useSalesData";
import { calculateSalesCampaign4PDP, type PdpSaleData } from "~/SalesCampaign/utils";
import { Price as PriceV3 } from "~/common/components/Price/v3/Price";
import { SaleDiscountIndicator } from "~/SalesCampaign/components/SaleDiscountIndicator/SaleDiscountIndicator";
import { SaleQuantityIndicator } from "~/SalesCampaign/components/SaleQuantityIndicator/SaleQuantityIndicator";
import { Controller } from "react-hook-form";
import { CheckboxWithLabel } from "~/common/components/CheckboxWithLabel";
import { InfoProductMessagePMA } from "~/ProductDetails/components/ProductSummary/InfoProductMessagePMA";
import { makeSalesAwarePackCodeProvider } from "~/SalesCampaign/salesAwarePackCodeProvider";
import { ProductDescriptorContext } from "~/feature/Product/ProductContext";
import { AddToCartButtonBase } from "~/feature/Product/elements/AddToCartButton";
import { BasicAddToWishlistButton } from "~/feature/Product/elements/AddToWishlistButton";
import { PackagingSelectionVariant } from "~/common/components/PackagingSelection/PackagingSelectionVariant";
import { zero } from "~/utils/PriceUtils";
import { selectSalesPrice } from "~/SalesCampaign/selectors";
import { useSelector } from "react-redux";
import { Product } from "~/feature/Product";
import { SearchTechValueTable } from "~/Search/components/SearchResults/ListView/SearchTechValueTable";
import { Box } from "@mui/material";
import { Alert } from "~/common/m3/Alert/Alert";
import { useResultTileZoovuParams } from "~/Search/utils/searchUtils";
import { usePurchasePlace, usePurchaseType } from "~/feature/Analytics/ga/recommendations/RecommendationContexts";
import { getPurchaseDataFromUrl } from "~/ProductDetails/components/ProductSummary/PdpSummaryHelper";
import { isTariffSurchargeEnabled } from "~/Cart/cartUtils";
import RichTooltip from "~/common/components/RichTooltip/RichTooltip";
import Cycle from "~/common/icons/Cycle";
import parse from "html-react-parser";

interface Props {
  isPdp?: boolean;
  productCode: string;
  index: number;
  onProductClick: (area: string, product: SrpProductData) => void;
  searchQuery: string;
}

export const SearchResultProductCustomer = memo((props: Props) => {
  return (
    <SRPPackageRestrictionContext productCode={props.productCode}>
      <SingleResultRow {...props} />
    </SRPPackageRestrictionContext>
  );
});

const SingleResultRow = ({ productCode, onProductClick, searchQuery, isPdp }: Props) => {
  const { t } = useTranslation();

  const reco_place = usePurchasePlace();
  const reco_type = usePurchaseType();

  const restrictedPackage = usePackageRestrictionContext();
  const [formProps, productDescriptor] = useAddToCartForm({
    productCode,
    initialPackCodeProvider: makeSalesAwarePackCodeProvider(restrictedPackage),
  });

  const salesData = useMemo(
    () => calculateSalesCampaign4PDP({ productDescriptor, includeSoldOut: true }),
    [productDescriptor],
  );

  const pageType = useSearchPageType();
  const gaLocation = getGALocationForSearchItem(isPdp, pageType);

  const product = productDescriptor.base;

  const tariffSurcharge = isTariffSurchargeEnabled() && formProps.watch("tariffSurcharge");

  const addToWishlistAvailable = canSeeWishlistButton();
  const addToCartAvailable = canSeeAddToCartButton();

  const selectedPack = formProps.watch("selectedPack");
  const packCode = formProps.watch("selectedPackCode");
  const packSize = selectedPack?.packSize ?? 1;

  const saleForPack: PdpSaleData | undefined = salesData.data?.salePerPack[packCode];
  const saleActive = !!saleForPack && !saleForPack.soldOut && formProps.watch("salePriceEnabled");

  const showSaleElements = !!saleForPack && (saleActive || saleForPack.soldOut);

  const { recommendationPlace, recommendationType } = getPurchaseDataFromUrl();

  const wishlistWizardProps = {
    productInput: product,
    initialQuantity: formProps.watch("quantity"),
    initialPriceReference: formProps.watch("priceReference"),
  };

  const doAddToCart = () => {
    const formValues = formProps.getValues();
    if (isPdp) {
      trackUserClickRecommendedProductSectionDetails(product.designation);
    }
    const context = {
      page: reco_place,
      recommendationPlace,
      recommendationType,
    };
    addToCartApi({
      restrictionQuery: restrictedPackage ?? "",
      productCode,
      ...formValues,
      addToCartSource: pageType === SearchPageType.SEARCH ? "srp" : "unknown",
      context,
    });
  };

  const productInformationClick = useCallback(
    () => onProductClick("Product information", product),
    [onProductClick, product],
  );

  const quantity = formProps.watch("quantity");

  let isPackageSizeInvalid = false;
  if (selectedPack) {
    isPackageSizeInvalid = quantity === 0 || quantity % selectedPack.packSize > 0;
  }

  const exceedsSalesStock = !!saleForPack && quantity > saleForPack.salesStock;

  const userCanAddToCart = getCanUserAddToCart(productDescriptor, selectedPack);

  const selectedPackageCode = formProps.watch("selectedPackCode");
  const onSelectPackage = (packCode: string) => {
    formProps.setValue("selectedPackCode", packCode);
    if (hasSales) {
      const soldOut = salesData.data?.salePerPack?.[packCode]?.soldOut ?? true;
      formProps.setValue("salePriceEnabled", !soldOut);
    }
  };

  const hasNotifications = isPackageSizeInvalid || exceedsSalesStock;

  const isSpecial = isSpecialPrice(productDescriptor, packCode);
  const hasSurcharge = getSurcharge({ product: productDescriptor, packCode, totalQuantity: packSize }) > 0;

  const webservicesDisabled = window.SCHA.config.disableAllWS;
  const addToCartDisabled =
    webservicesDisabled ||
    !userCanAddToCart ||
    isPackageSizeInvalid ||
    productDescriptor.isLoading ||
    productDescriptor.isError;

  const priceReference = formProps.watch("priceReference");
  const priceParams: SelectNetPriceProps = {
    packCode,
    extraQuantity: quantity,
    priceReference,
    productCode,
  };
  const price = formProps.watch("netPrice");
  const salesPrice = useSelector(selectSalesPrice({ ...priceParams, salesData: saleForPack })) ?? zero();

  let regularPriceValid = price && price.value > 0;
  if (saleActive) {
    regularPriceValid = regularPriceValid && price!.value !== (salesPrice.value ?? 0);
  }
  const salePriceValid = salesPrice.value > 0;

  const showListPrice =
    canSeeListPrice({
      product: productDescriptor,
      hasSurcharge: hasSurcharge,
      isSpecialPrice: isSpecial,
    }) && !saleActive;

  const hasSales = salesData.data?.hasSales;
  const someSaleHasStock = Object.values(salesData.data?.salePerPack ?? {}).some((sale) => !sale.soldOut);
  const tagColour = someSaleHasStock ? "active" : "inactive";

  const pdpLinkProps = {
    onClick: productInformationClick,
    reco_type: recommendationType,
    reco_place: recommendationPlace,
  };

  return (
    <SalesDataProvider value={salesData.data}>
      <ProductDescriptorContext value={productDescriptor}>
        <Card
          className={classNames(classes.root, "jsSearchResultProduct")}
          data-test-id={`search-result-item`}
          {...useResultTileZoovuParams(product)}
        >
          {hasSales && <SaleTag className={classes.salesTag} variant={tagColour} size={48} />}
          <CardContent>
            <ProductDetailsLayout
              imageSlot={
                <Product.PdpLink {...pdpLinkProps}>
                  <Product.PrimaryImageWithLogos className={classes.image} clickable={false} />
                </Product.PdpLink>
              }
              productDataSlot={
                <div className={classes.dataCol}>
                  <Product.PdpLink {...pdpLinkProps}>
                    <Product.FullDesignation clickable={false} className={classes.designation} />
                  </Product.PdpLink>

                  <Product.PdpLink {...pdpLinkProps}>
                    <Product.ShortDescription clickable={false} />
                  </Product.PdpLink>

                  {product.reconditionable && (
                    <RichTooltip
                      chipText={t("cmsnavigationnode.schaefflerContentCatalog.reconditionable")}
                      message={parse(t("cmsnavigationnode.schaefflerContentCatalog.reconditionableInfo"))}
                      linkText={t("cmsnavigationnode.schaefflerContentCatalog.moreInfo")}
                      ChipIcon={<Cycle fontSize="small" />}
                      variant="success"
                    />
                  )}

                  <NewVersionMessage className={classes.newVersionMessage} baseProduct={product} />

                  <SearchPageParamContainer>
                    <OldDesignationParameter variant={"search-page"} label={t("product.oldDesignations")} />
                    <SchaefflerIdParameter variant={"search-page"} />
                    <AliasesParameter variant={"search-page"} mode={"13-digits"} />
                  </SearchPageParamContainer>
                </div>
              }
              priceDataSlot={
                <div className={classes.priceCol}>
                  {productDescriptor.isLoading && (
                    <div className={"mb-30"}>
                      <CircleSpinner />
                    </div>
                  )}
                  {productDescriptor.isLoaded && (
                    <>
                      <div className={classes.netPriceSection}>
                        {!regularPriceValid && !saleActive && (
                          <span
                            className={classNames({
                              [classes.regularPrice]: true,
                              [classes.priceOnRequest]: true,
                              [classes.mainPrice]: !saleActive,
                            })}
                          >
                            {t("product.priceOnRequest")}
                          </span>
                        )}
                        {regularPriceValid && (
                          <PriceV3
                            className={classNames({
                              [classes.regularPrice]: true,
                              [classes.mainPrice]: !saleActive && !showSaleElements,
                              [classes.active]: !saleActive,
                            })}
                            tariffSurcharge={tariffSurcharge}
                            price={price}
                          />
                        )}
                        {(regularPriceValid || salePriceValid) && (
                          <div className={classes.priceLabels}>
                            <PriceV3.NetPriceUnit price={price} surchargeEnabled={false} />
                            {tariffSurcharge && <p className="label-small">{t("price.tariff.surcharge.applicable")}</p>}
                            {!saleActive && (
                              <PriceV3.ExtraLabels
                                className={classes.extraPriceLabels}
                                price={price}
                                surchargeEnabled={false}
                              />
                            )}
                          </div>
                        )}
                        {showSaleElements && (
                          <PriceV3
                            className={classNames({
                              [classes.salePrice]: true,
                              [classes.active]: saleActive,
                            })}
                            tariffSurcharge={tariffSurcharge}
                            price={salesPrice}
                          />
                        )}
                        {showSaleElements && regularPriceValid && (
                          <SaleDiscountIndicator
                            className={classes.discount}
                            regularPrice={price}
                            salePrice={salesPrice}
                            soldOut={saleForPack?.soldOut}
                          />
                        )}
                      </div>
                    </>
                  )}

                  {showListPrice && <ListPrice product={productDescriptor} packCode={packCode} />}
                  <GPSDParameter product={productDescriptor.base} />
                </div>
              }
              actionSlot={
                <div className={classes.actionCol}>
                  {showSaleElements && (
                    <SaleQuantityIndicator className={"mr-auto"} quantity={saleForPack.salesStock} />
                  )}

                  {hasSales && (
                    <Controller
                      control={formProps.control}
                      name={"salePriceEnabled"}
                      render={({ field: { value, onChange } }) => (
                        <CheckboxWithLabel
                          checked={value}
                          onCheck={onChange}
                          hidden={!saleForPack || !!priceReference || saleForPack.soldOut}
                        >
                          {t("feature.salesCampaign.specialPrice")}
                        </CheckboxWithLabel>
                      )}
                    />
                  )}

                  <PriceReferenceParam
                    product={product}
                    priceReference={priceReference}
                    data-test-id={"plp-srp-list-item-price-reference"}
                    onChange={(value) => formProps.setValue("priceReference", value ?? "")}
                    editable={canSeePriceRefColumn()}
                    disabled={saleActive}
                  />

                  <QuantityInput
                    min={packSize}
                    max={MAX_QUANTITY}
                    value={quantity}
                    disabled={productDescriptor.isLoading || !product.quantityVisible}
                    step={packSize}
                    data-test-id={"plp-srp-list-item-input-quantity"}
                    onValueChange={(value) =>
                      formProps.setValue("quantity", value, { shouldTouch: true, shouldDirty: true })
                    }
                    error={isPackageSizeInvalid}
                  />
                </div>
              }
              techTableSlot={<SearchTechValueTable features={productDescriptor.base.features} />}
              buttonsSlot={
                <div className={classes.buttonRow}>
                  {isProductComparisonCheckboxVisible(product) && (
                    <ProductComparisonCheckbox productCode={productCode} gaLocation={gaLocation} />
                  )}
                  <Box
                    sx={{
                      display: "flex",
                      gap: 2,
                    }}
                  >
                    {addToWishlistAvailable && (
                      <BasicAddToWishlistButton
                        sideButton={true}
                        size="medium"
                        color={"secondary"}
                        wizardProps={wishlistWizardProps}
                      />
                    )}
                    {addToCartAvailable &&
                      (isProductPMA(productDescriptor) ? (
                        <InfoProductMessagePMA short={true} onClick={() => redirectToUrl(product.url)} />
                      ) : (
                        <AddToCartButtonBase
                          size="medium"
                          className={classes.cartBtn}
                          disabled={addToCartDisabled}
                          onClick={doAddToCart}
                        />
                      ))}
                  </Box>
                </div>
              }
            />
            {hasNotifications && (
              <div className={classes.notifications}>
                {isPackageSizeInvalid && <Alert severity="error">{t("addToCartPopup.mismatchedQty")}</Alert>}
                {exceedsSalesStock && <Alert severity="info">{t("feature.salesCampaign.exceedsQty")}</Alert>}
              </div>
            )}
          </CardContent>

          {product.quantityVisible && (
            <CardContent
              className={classNames(
                classes.packagingSelection,
                productDescriptor.isAvailabilityError && classes.packagingSelectionResetHeight,
                isProductPMA(productDescriptor) && "hidden",
              )}
            >
              {productDescriptor.isAvailabilityLoading && (
                <CenterContents>
                  <CircleSpinner />
                </CenterContents>
              )}
              {!webservicesDisabled && productDescriptor.isAvailabilityError && (
                <CenterContents>
                  <Alert severity={"error"}>{t("hybrisAvailabilityError")}</Alert>
                </CenterContents>
              )}

              <div hidden={!productDescriptor.availability || isProductPMA(productDescriptor)}>
                <PackagingSelection
                  selectedCode={selectedPackageCode}
                  onSelectCode={onSelectPackage}
                  product={productDescriptor}
                  variant={PackagingSelectionVariant.SRP}
                />
              </div>
            </CardContent>
          )}
        </Card>
      </ProductDescriptorContext>
    </SalesDataProvider>
  );
};
